/**
 * Remove todos os caracteres nao alfanumericos (preserva letras e digitos).
 * Util para limpar CPF/CNPJ antes de enviar para API — suporta CNPJ alfanumerico.
 */
export function unformatCpfCnpj(value: string): string {
  return (value || '').replace(/[^0-9A-Za-z]/g, '').toUpperCase();
}

/**
 * Formata uma string como CPF (000.000.000-00) ou CNPJ (00.000.000/0000-00).
 * Suporta CNPJ alfanumerico (novo padrao Receita Federal a partir de 07/2026):
 * - CPF: apenas digitos (11 caracteres)
 * - CNPJ: primeiros 12 caracteres alfanumericos [0-9A-Z], ultimos 2 digitos numericos
 * Suporta formatacao progressiva enquanto o usuario digita.
 */
export function formatCpfCnpj(value: string): string {
  if (!value) return '';

  // Limpa — mantem apenas alfanumericos, em MAIUSCULAS
  const clean = unformatCpfCnpj(value).slice(0, 14);

  // Se tem qualquer letra, tratamos como CNPJ (CPF nao tem letras)
  const temLetra = /[A-Z]/.test(clean);

  // ate 11 caracteres numericos: CPF
  if (!temLetra && clean.length <= 11) {
    // CPF: 000.000.000-00
    const digits = clean.replace(/\D/g, '');
    if (digits.length <= 3) return digits;
    if (digits.length <= 6) return `${digits.slice(0, 3)}.${digits.slice(3)}`;
    if (digits.length <= 9) return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
    return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
  }

  // CNPJ (numerico ou alfanumerico): 00.000.000/0000-00 (ate 14 caracteres, ultimos 2 sao numericos)
  const body = clean.slice(0, 12); // 12 primeiros podem ser alfanumericos
  const dv = clean.slice(12, 14).replace(/\D/g, ''); // ultimos 2 sao sempre numericos

  let formatted = body.slice(0, 2);
  if (body.length > 2) formatted += '.' + body.slice(2, 5);
  if (body.length > 5) formatted += '.' + body.slice(5, 8);
  if (body.length > 8) formatted += '/' + body.slice(8, 12);
  if (dv.length > 0) formatted += '-' + dv;

  return formatted;
}

/**
 * Formata uma string de digitos como telefone fixo ou celular.
 * Suporta formatacao progressiva enquanto o usuario digita.
 */
export function formatPhone(value: string): string {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  if (digits.length <= 6) return `(${digits.slice(0, 2)}) ${digits.slice(2)}`;
  if (digits.length <= 10) {
    return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
  }
  return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
}

/**
 * Remove todos os caracteres nao numericos de uma string.
 * @deprecated Use unformatCpfCnpj para CPF/CNPJ (suporta alfanumerico).
 */
export function unformat(value: string): string {
  return value.replace(/\D/g, '');
}

export const PHONE_MASK = '(99) 99999-9999';

/**
 * Valida CPF (11 digitos numericos) conferindo os digitos verificadores.
 */
export function validarCPF(cpf: string): boolean {
  const doc = unformatCpfCnpj(cpf);
  if (!/^\d{11}$/.test(doc)) return false;
  if (/^(\d)\1{10}$/.test(doc)) return false;

  for (const posicao of [9, 10]) {
    let soma = 0;
    for (let i = 0; i < posicao; i++) soma += Number(doc[i]) * (posicao + 1 - i);
    let dv = (soma * 10) % 11;
    if (dv === 10) dv = 0;
    if (dv !== Number(doc[posicao])) return false;
  }
  return true;
}

const PESOS_CNPJ_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const PESOS_CNPJ_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

function valorCaractere(c: string): number {
  return c.charCodeAt(0) - 48;
}

/**
 * Valida CNPJ (numerico ou alfanumerico) conferindo os digitos verificadores.
 * Segue a regra alfanumerica da RFB (07/2026): 12 primeiros caracteres [0-9A-Z],
 * DV numerico calculado sobre (codigo ASCII - 48) de cada caractere.
 */
export function validarCNPJ(cnpj: string): boolean {
  const doc = unformatCpfCnpj(cnpj);
  if (doc.length !== 14) return false;
  if (!/^[0-9A-Z]{12}\d{2}$/.test(doc)) return false;
  if (/^(.)\1{13}$/.test(doc)) return false;

  const calcularDV = (base: string, pesos: number[]): number => {
    let soma = 0;
    for (let i = 0; i < base.length; i++) soma += valorCaractere(base[i]) * pesos[i];
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  const dv1 = calcularDV(doc.substring(0, 12), PESOS_CNPJ_DV1);
  if (dv1 !== Number(doc[12])) return false;

  const dv2 = calcularDV(doc.substring(0, 13), PESOS_CNPJ_DV2);
  return dv2 === Number(doc[13]);
}

/**
 * Valida se o valor e um CPF ou CNPJ valido (com digitos verificadores).
 * Com letra trata como CNPJ; sem letra decide pelo tamanho (11=CPF, 14=CNPJ).
 */
export function isCpfCnpjValid(value: string): boolean {
  const clean = unformatCpfCnpj(value);
  if (/[A-Z]/.test(clean)) return validarCNPJ(clean);
  if (clean.length === 11) return validarCPF(clean);
  if (clean.length === 14) return validarCNPJ(clean);
  return false;
}
